import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { invoiceService } from '../services/invoice.service';
import { servicesService } from '../services/services.service';
import InvoiceCard from '../components/InvoiceCard';
import InvoiceDetail from '../components/InvoiceDetail';
import ServiceDetailsModal from '../components/ServiceDetailsModal';

const Invoices = () => {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeFilter, setActiveFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [downloadingId, setDownloadingId] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [, setDetailLoading] = useState(false);
    const [selectedService, setSelectedService] = useState(null);
    const [serviceModalOpen, setServiceModalOpen] = useState(false);
    const [serviceLoading, setServiceLoading] = useState(false);

    const limit = 20;

    const loadInvoices = useCallback(async (isRefresh = false) => {
        if (!user?.vendor_id) return;

        try {
            setLoading(!isRefresh);
            setError(null);

            const params = {
                page: currentPage,
                limit: limit
            };

            // Add status filter if not 'all'
            if (activeFilter !== 'all') {
                params.status = activeFilter;
            }

            const response = await invoiceService.getInvoices(user.vendor_id, params);
            
            setInvoices(response.items || []);
            setTotalPages(response.pages || 1);
            setTotalItems(response.total || 0);
        } catch (err) {
            console.error('Error loading invoices:', err);
            setError(err.message || 'Failed to load invoices');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [user?.vendor_id, activeFilter, currentPage]);

    useEffect(() => {
        if (user?.vendor_id) {
            loadInvoices();
        }
    }, [loadInvoices, user?.vendor_id]);

    const handleRefresh = () => {
        setRefreshing(true);
        loadInvoices(true);
    };

    const handleDownload = async (invoiceId) => {
        setDownloadingId(invoiceId);
        try {
            const { download_url } = await invoiceService.getInvoiceDownloadLink(invoiceId);
            if (!download_url) {
                alert('PDF is not ready yet. Try "Generate PDF" first.');
                return;
            }
            const link = document.createElement('a');
            link.href = download_url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.download = `vendor-invoice-${invoiceId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading invoice:', err);
            alert('Failed to download invoice. Please try again.');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleSubmit = async (invoice, extra, notes) => {
        setBusyId(invoice.id);
        try {
            await invoiceService.updateExtras(invoice.id, extra, notes);
            await invoiceService.confirm(invoice.id);
            await loadInvoices(true);
        } catch (err) {
            console.error('Error submitting invoice:', err);
            alert(err?.response?.data?.detail || 'Failed to submit invoice. Please try again.');
        } finally {
            setBusyId(null);
        }
    };

    const handleRegeneratePdf = async (invoiceId) => {
        setBusyId(invoiceId);
        try {
            await invoiceService.regeneratePdf(invoiceId);
            await loadInvoices(true);
        } catch (err) {
            console.error('Error generating PDF:', err);
            alert(err?.response?.data?.detail || 'Could not generate the PDF. Please try again.');
        } finally {
            setBusyId(null);
        }
    };

    const handleViewDetails = async (invoiceId) => {
        setDetailLoading(true);
        try {
            const invoiceDetail = await invoiceService.getInvoiceDetail(invoiceId);
            setSelectedInvoice(invoiceDetail);
        } catch (err) {
            console.error('Error loading invoice details:', err);
            alert('Failed to load invoice details. Please try again.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetail = () => {
        setSelectedInvoice(null);
    };

    const handleViewService = async (serviceId) => {
        setServiceLoading(true);
        try {
            const serviceDetail = await servicesService.getServiceDetail(serviceId);
            setSelectedService(serviceDetail);
            setServiceModalOpen(true);
        } catch (err) {
            console.error('Error loading service details:', err);
            alert('Failed to load service details. Please try again.');
        } finally {
            setServiceLoading(false);
        }
    };

    const handleCloseServiceModal = () => {
        setServiceModalOpen(false);
        setSelectedService(null);
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
        setCurrentPage(1); // Reset to first page when filter changes
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        if (currentPage > 1) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50"
                >
                    Previous
                </button>
            );
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-2 text-sm font-medium border ${
                        i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                >
                    {i}
                </button>
            );
        }

        // Next button
        if (currentPage < totalPages) {
            pages.push(
                <button
                    key="next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50"
                >
                    Next
                </button>
            );
        }

        return (
            <div className="flex justify-center mt-6 overflow-x-auto">
                <nav className="flex flex-wrap justify-center gap-1 sm:gap-0" aria-label="Pagination">
                    {pages}
                </nav>
            </div>
        );
    };

    if (!user?.vendor_id) {
        return (
            <div className="page-container">
                <div className="content-container">
                    <div className="card">
                        <div className="text-center py-12">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                Vendor ID Not Found
                            </h2>
                            <p className="text-gray-600">
                                Unable to load invoices. Please contact support.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="content-container">
                {/* Header */}
                <div className="card mb-4 md:mb-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                                Invoices Management
                            </h1>
                            <p className="text-gray-600 mt-1 text-sm md:text-base">
                                View and manage your billing and payments
                            </p>
                        </div>
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 w-full sm:w-auto"
                        >
                            {refreshing ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Refreshing...
                                </>
                            ) : (
                                <>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Refresh
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="card mb-4 md:mb-6">
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
                        {[
                            { key: 'all', label: 'All' },
                            { key: 'draft', label: 'Draft' },
                            { key: 'pending', label: 'Pending' },
                            { key: 'approved', label: 'Approved' },
                            { key: 'processed', label: 'Paid' },
                            { key: 'rejected', label: 'Rejected' }
                        ].map((filter) => (
                            <button
                                key={filter.key}
                                onClick={() => handleFilterChange(filter.key)}
                                className={`shrink-0 py-2 px-3 md:px-4 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                                    activeFilter === filter.key
                                        ? 'bg-white text-blue-600 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {filter.key === 'all' ? (
                                    <>
                                        <span className="md:hidden">All</span>
                                        <span className="hidden md:inline">All Invoices</span>
                                    </>
                                ) : (
                                    filter.label
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
                    <div className="card">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-gray-900">{totalItems}</div>
                            <div className="text-sm text-gray-600">Total Invoices</div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                                {invoices.filter(inv => inv.status === 'draft').length}
                            </div>
                            <div className="text-sm text-gray-600">Draft</div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                                {invoices.filter(inv => inv.status === 'processed').length}
                            </div>
                            <div className="text-sm text-gray-600">Paid</div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-purple-600">
                                {invoices.filter(inv => inv.status === 'approved').length}
                            </div>
                            <div className="text-sm text-gray-600">Approved</div>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="card mb-6 border-red-200 bg-red-50">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="text-red-800">{error}</span>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="card">
                        <div className="text-center py-12">
                            <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <p className="text-gray-600">Loading invoices...</p>
                        </div>
                    </div>
                )}

                {/* Invoices List */}
                {!loading && !error && (
                    <>
                        {invoices.length > 0 ? (
                            <div className="space-y-6">
                                {invoices.map((invoice) => (
                                    <InvoiceCard
                                        key={invoice.id}
                                        invoice={invoice}
                                        onDownload={handleDownload}
                                        onView={handleViewDetails}
                                        onViewService={handleViewService}
                                        onSubmit={handleSubmit}
                                        onRegeneratePdf={handleRegeneratePdf}
                                        downloading={downloadingId === invoice.id}
                                        busy={busyId === invoice.id}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="card">
                                <div className="text-center py-12">
                                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                                        <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                        No Invoices Found
                                    </h2>
                                    <p className="text-gray-600 max-w-md mx-auto">
                                        {activeFilter === 'all' 
                                            ? 'No invoices have been generated yet.'
                                            : `No ${activeFilter} invoices found.`
                                        }
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {renderPagination()}
                    </>
                )}

                {/* Invoice Detail Modal */}
                {selectedInvoice && (
                    <InvoiceDetail
                        invoice={selectedInvoice}
                        onClose={handleCloseDetail}
                        onDownload={handleDownload}
                        downloading={downloadingId === selectedInvoice.id}
                    />
                )}

                {/* Service Details Modal */}
                {selectedService && (
                    <ServiceDetailsModal
                        service={selectedService}
                        isOpen={serviceModalOpen}
                        onClose={handleCloseServiceModal}
                    />
                )}

                {/* Service Loading Overlay */}
                {serviceLoading && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                            <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-gray-700">Loading service details...</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Invoices;
